import { EiffageScan, ScanSchema } from '@willie/schemas'
import { Tables, TablesInsert } from '@willie/supabase'
import { createOrUpdateDatasource } from '~/features/stations/datasources-service'
import { getWillieSupabaseClient } from '~/utils'

/**
 * Parse a database record from the plugin_eiffage_scans table into a Scan object
 *
 * @param record The database record to parse
 * @returns A validated Scan object
 * @throws Error if the record cannot be parsed according to the schema
 */
export function parseScanRecord(record: Tables<'plugin_eiffage_scans'>, options?: { partial?: false }): EiffageScan
export function parseScanRecord(record: Tables<'plugin_eiffage_scans'>, options: undefined): EiffageScan
export function parseScanRecord(
  record: Tables<'plugin_eiffage_scans'>,
  options?: { partial: true }
): Partial<EiffageScan>
export function parseScanRecord(
  record: Tables<'plugin_eiffage_scans'>,
  options: { partial?: boolean } = {}
): EiffageScan | Partial<EiffageScan> {
  try {
    const schema = options.partial ? ScanSchema.partial() : ScanSchema
    return schema.parse({
      ID: record.id,
      name: record.name,
      createdAt: record.created_at,
      processedAt: record.processed_at,
      orgID: record.org_id,
      status: record.status,
      extractedData: record.extracted_data,
      verifiedData: record.validated_data
    })
  } catch (error) {
    console.error(`Error parsing scan record with ID ${record.id}:`, error)
    throw new Error(`Invalid scan data format for ID ${record.id}`)
  }
}

export const updateScanStatus = async (scanID: EiffageScan['ID'], status: EiffageScan['status']) => {
  const supabase = getWillieSupabaseClient()
  const { error } = await supabase.from('plugin_eiffage_scans').update({ status }).eq('id', scanID)
  if (error) {
    console.error(`Error updating scan status for ${scanID}:`, error)
    throw new Error(`Error updating scan status for ${scanID}`)
  }
  return { scanID, status }
}

export const createScan = async (orgID: string, file: File) => {
  const supabase = getWillieSupabaseClient()

  const { data: scan, error: errorScan } = await supabase
    .from('plugin_eiffage_scans')
    .insert([
      {
        org_id: orgID,
        status: 'pending',
        name: file.name
      }
    ])
    .select('id')
    .single()

  if (errorScan) {
    console.error('Error creating scan record:', errorScan)
    throw new Error('Error creating scan record')
  }
  const scanID = scan.id

  // TODO replace this with the new upload function in the org service
  const { error } = await supabase.storage.from('org_content').upload(`${orgID}/scans/${scanID}`, file)
  if (error) {
    console.error('Error uploading scan file:', error)
    throw new Error('Error uploading scan file')
  }

  return { scanID }
}

export const getScan = async (scanID: string): Promise<EiffageScan> => {
  const supabase = getWillieSupabaseClient()
  const { data, error } = await supabase.from('plugin_eiffage_scans').select('*').eq('id', scanID).single()

  if (error) {
    console.error(`Error fetching scan ${scanID}:`, error)
    throw new Error(`Error fetching scan ${scanID}`)
  }

  return parseScanRecord(data)
}
interface ListScansOptions {
  page?: number
  pageSize?: number
}

const DEFAULT_LIST_SCANS_OPTIONS = {
  page: 0,
  pageSize: 25
} as const

export const listScans = async (orgID: string, options: ListScansOptions = {}): Promise<EiffageScan[]> => {
  const supabase = getWillieSupabaseClient()
  const { page, pageSize } = { ...DEFAULT_LIST_SCANS_OPTIONS, ...options }
  const from = page * pageSize
  const to = from + pageSize - 1

  const { data, error } = await supabase
    .from('plugin_eiffage_scans')
    .select('*')
    .eq('org_id', orgID)
    .order('created_at', { ascending: false })
    .range(from, to)

  if (error) {
    console.error(`Error listing scans for org ${orgID}:`, error)
    throw new Error(`Error listing scans for org ${orgID}`)
  }

  return data.map((r) => parseScanRecord(r))
}

export const importScanData = async (scan: EiffageScan) => {
  const supabase = getWillieSupabaseClient()

  if (scan.verifiedData === null) {
    throw new Error('Scan data is missing extractedData ')
  }

  if (scan.status !== 'validation') {
    throw new Error(`Scan status is not 'requires_validation', current status: ${scan.status}`)
  }

  for (const pumpStation of scan.verifiedData.metadata.pumpStations) {
    //todo add secured attachements how do we handle this?
    const datasource = await createOrUpdateDatasource({
      config: {},
      sourceType: 'manual',
      readingMethod: 'incremental',
      unit: 'm3',
      stationID: pumpStation.id,
      notes: `Fiche de prélèvement pour la semaine No: ${scan.verifiedData.metadata.weekNumber}, remplie par: ${scan.verifiedData.metadata.driverName}`,
      attachments: [`${scan.orgID}/scans/${scan.ID}`]
    })

    // TODO call insertManualDatapoints instead of this, it's pretty much a little refact
    const datapoints = scan.verifiedData.readings
      .filter((reading) => reading.pumpStationID === pumpStation.id)
      .map<TablesInsert<'data-lake'>>((reading) => ({
        payload: {
          consumption: reading.volume,
          raw: { ...reading, stationName: pumpStation.name }
        },
        created_at: reading.date,
        device_id: datasource.id,
        org_id: scan.orgID
      }))

    // @ts-ignore
    if (datapoints.filter((dp) => dp.payload?.consumption ?? 0 > 0).length === 0) {
      console.warn(`No datapoints found for pump station ${pumpStation.id} in scan ${scan.ID}`)
      continue
    }
    const { error: insertError } = await supabase.from('data-lake').insert(datapoints)
    if (insertError) {
      console.error(`Error inserting data for pump station ${pumpStation.id} in scan ${scan.ID}:`, insertError)
      throw new Error(`Error inserting data for pump station ${pumpStation.id} in scan ${scan.ID}`)
    }
  }

  await supabase
    .from('plugin_eiffage_scans')
    .update({
      status: 'completed',
      validated_data: scan.verifiedData
    })
    .eq('id', scan.ID)
}
